import React from "react";
import { Paper, Typography, Divider } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Skeleton } from "@material-ui/lab";

// Card genérico para listas em telas pequenas — substitui tabelas no mobile.
// Segue o mesmo visual dos cards da tela Configurações > Empresas.
//
// Uso:
//   <MobileRecordCard
//     title="Mensalidade João"
//     subtitle="João da Silva"
//     headerAction={<IconButton .../>}
//     body={<Typography>Texto longo em largura total…</Typography>}
//     rows={[{ label: "Valor", value: "R$ 100,00" }]}
//     chips={<><Chip .../><Chip .../></>}
//     footer={<Button fullWidth .../>}
//     highlight   // borda/faixa vermelha (item vencido/atrasado)
//   />

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
  },
  cardHighlight: {
    borderColor: theme.palette.type === "dark" ? "#e57373" : "#ef9a9a",
    boxShadow: `inset 3px 0 0 ${theme.palette.type === "dark" ? "#e57373" : "#c62828"}`,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(1),
    gap: theme.spacing(1),
  },
  title: {
    fontWeight: 600,
    fontSize: "0.95rem",
    wordBreak: "break-word",
  },
  headerAction: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  rowLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    fontWeight: 500,
    flexShrink: 0,
  },
  rowValue: {
    fontSize: "0.82rem",
    textAlign: "right",
    wordBreak: "break-word",
  },
  body: {
    marginBottom: theme.spacing(1),
  },
  chips: {
    display: "flex",
    gap: theme.spacing(0.5),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
  footer: {
    marginTop: theme.spacing(1.5),
  },
}));

export function MobileRecordCardSkeleton() {
  const classes = useStyles();
  return (
    <Paper className={classes.card} elevation={0}>
      <Skeleton variant="text" width="60%" height={24} />
      <Skeleton variant="text" width="40%" />
      <Skeleton variant="rect" height={56} style={{ marginTop: 8, borderRadius: 4 }} />
    </Paper>
  );
}

const MobileRecordCard = ({
  title,
  subtitle,
  headerAction,
  body,
  rows = [],
  chips,
  footer,
  highlight = false,
}) => {
  const classes = useStyles();

  return (
    <Paper
      className={`${classes.card} ${highlight ? classes.cardHighlight : ""}`}
      elevation={0}
    >
      <div className={classes.header}>
        <div style={{ minWidth: 0 }}>
          <Typography className={classes.title}>{title}</Typography>
          {subtitle && (
            <Typography variant="caption" color="textSecondary">
              {subtitle}
            </Typography>
          )}
        </div>
        {headerAction && <div className={classes.headerAction}>{headerAction}</div>}
      </div>

      {body && <div className={classes.body}>{body}</div>}

      {rows.length > 0 && <Divider style={{ marginBottom: 8 }} />}

      {rows.map((row) => (
        <div className={classes.row} key={row.label}>
          <Typography className={classes.rowLabel}>{row.label}</Typography>
          <Typography component="div" className={classes.rowValue}>
            {row.value ?? "—"}
          </Typography>
        </div>
      ))}

      {chips && <div className={classes.chips}>{chips}</div>}
      {footer && <div className={classes.footer}>{footer}</div>}
    </Paper>
  );
};

export default MobileRecordCard;
